import { Fragment, useEffect, useId, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";
import { Trans, useTranslation } from "react-i18next";
import {
  IconChevronRight,
  IconFileText,
  IconHash,
  IconPlaylist,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import { Avatar } from "../ui/Avatar";
import Skeleton from "../skeleton/Skeleton";

/** One selectable entry of the palette. `key` is unique across the palette. */
type DesktopSearchPaletteItem =
  | { kind: "ask"; key: string; question: string }
  | {
      kind: "article";
      key: string;
      title: string;
      journalName: string | null;
      impactFactor: number | null;
      date: string;
    }
  | { kind: "collection"; key: string; title: string; meta: string }
  | {
      kind: "room";
      key: string;
      name: string;
      logoUrl: string | null;
      meta: string;
    }
  | { kind: "topic"; key: string; name: string; meta: string }
  | { kind: "topic-chip"; key: string; name: string }
  | { kind: "see-all"; key: string; label: string };

/**
 * A titled block of items. `title: null` is the untitled first block (Ask).
 *
 * A `loading` block is a search still in flight: its title and the silhouette
 * of the rows it will hold. It has no items, so the keyboard never lands on a
 * row that is not there yet.
 */
type DesktopSearchPaletteGroup =
  | {
      status: "ready";
      key: string;
      title: string | null;
      items: readonly DesktopSearchPaletteItem[];
    }
  | {
      status: "loading";
      key: string;
      title: string;
      /** `article` puts the journal line above the title, as an article row does. */
      shape: "article" | "entry";
      rows: number;
    };

// ── Highlight ───────────────────────────────────────────────────────────

function fold(char: string): string {
  return char
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/**
 * The text cut into runs, the ones matching a word of the query marked. Case
 * and accents are ignored ("insuffisance cardiaque" matches "Insuffisance
 * Cardiaque"), and each run keeps the original characters.
 */
function highlightRuns(
  text: string,
  query: string
): { text: string; match: boolean }[] {
  const words = query
    .split(/\s+/u)
    .map((word) => [...word].map(fold).join(""))
    .filter((word) => word.length >= 2);
  if (words.length === 0) return [{ text, match: false }];

  // The folded text, and for each of its characters the index of the original
  // character it came from, so a match maps back to the original text.
  const chars = [...text];
  let folded = "";
  const origin: number[] = [];
  for (const [index, char] of chars.entries()) {
    const f = fold(char);
    folded += f;
    for (let i = 0; i < f.length; i += 1) origin.push(index);
  }

  const marked = chars.map(() => false);
  for (const word of words) {
    let from = folded.indexOf(word);
    while (from !== -1) {
      for (let i = from; i < from + word.length; i += 1) {
        const index = origin[i];
        if (index === undefined)
          throw new Error("should not happen: a folded char without origin");
        marked[index] = true;
      }
      from = folded.indexOf(word, from + word.length);
    }
  }

  const runs: { text: string; match: boolean }[] = [];
  for (const [index, char] of chars.entries()) {
    const match = marked[index] === true;
    const last = runs.at(-1);
    if (last !== undefined && last.match === match) last.text += char;
    else runs.push({ text: char, match });
  }
  return runs;
}

function Highlighted({
  text,
  query,
}: {
  text: string;
  query: string;
}): React.JSX.Element {
  return (
    <>
      {highlightRuns(text, query).map((run, index) =>
        run.match ? (
          <strong key={index} className="font-semibold">
            {run.text}
          </strong>
        ) : (
          <Fragment key={index}>{run.text}</Fragment>
        )
      )}
    </>
  );
}

// ── Pieces ──────────────────────────────────────────────────────────────

function Kbd({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <kbd className="shrink-0 rounded-md border border-ds-slate-100 bg-ds-slate-100 px-1.5 py-px font-dm-sans text-caption text-ds-slate-700">
      {children}
    </kbd>
  );
}

function Tile({
  tone,
  children,
}: {
  tone: "slate" | "mint";
  children: ReactNode;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-lg text-ds-slate-900",
        tone === "mint" ? "bg-ds-mint-100" : "bg-ds-slate-50"
      )}
    >
      {children}
    </span>
  );
}

function ItemBody({
  item,
  query,
}: {
  item: Exclude<DesktopSearchPaletteItem, { kind: "topic-chip" | "see-all" }>;
  query: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  switch (item.kind) {
    case "ask":
      return (
        <>
          <Tile tone="mint">
            <IconSparkles size={16} stroke={1.25} />
          </Tile>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1">
              <span className="shrink-0 text-footnote font-semibold text-ds-primary">
                {t("Ask JuisciGPT")}
              </span>
              <span className="min-w-0 truncate text-ui-body text-ds-black">
                “{item.question}”
              </span>
            </span>
            <span className="text-caption text-ds-slate-600">
              {t("Get a sourced answer from the literature")}
            </span>
          </span>
        </>
      );
    case "article":
      return (
        <>
          <Tile tone="slate">
            <IconFileText size={16} stroke={1.25} />
          </Tile>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex min-w-0 items-center gap-1 text-caption text-ds-slate-600">
              {item.journalName === null ? null : (
                <Trans
                  i18nKey="In <name>{{name}}</name>"
                  values={{ name: item.journalName }}
                  components={{
                    name: (
                      <span className="min-w-0 truncate font-medium text-ds-primary" />
                    ),
                  }}
                />
              )}
              {item.impactFactor === null ? null : (
                <span className="shrink-0 rounded bg-ds-slate-50 px-2 py-1 font-medium">
                  {t("IF {{value}}", { value: item.impactFactor.toFixed(1) })}
                </span>
              )}
              <span className="shrink-0">· {item.date}</span>
            </span>
            <span className="text-ui-body text-ds-black">
              <Highlighted text={item.title} query={query} />
            </span>
          </span>
        </>
      );
    case "collection":
      return (
        <>
          <Tile tone="slate">
            <IconPlaylist size={16} stroke={1.25} />
          </Tile>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-ui-body text-ds-black">
              <Highlighted text={item.title} query={query} />
            </span>
            <span className="text-caption text-ds-slate-600">{item.meta}</span>
          </span>
        </>
      );
    case "room":
      return (
        <>
          {item.logoUrl === null ? (
            <Avatar
              variant="initials"
              initials={item.name.charAt(0)}
              size="md"
              badge={null}
            />
          ) : (
            <Avatar
              variant="image"
              src={item.logoUrl}
              alt=""
              imageFit="scale-down"
              size="md"
              badge={null}
            />
          )}
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-ui-body text-ds-black">
              <Highlighted text={item.name} query={query} />
            </span>
            <span className="text-caption text-ds-slate-600">{item.meta}</span>
          </span>
        </>
      );
    case "topic":
      return (
        <>
          <Tile tone="slate">
            <IconHash size={16} stroke={1.25} />
          </Tile>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-ui-body text-ds-black">
              <Highlighted text={item.name} query={query} />
            </span>
            <span className="text-caption text-ds-slate-600">{item.meta}</span>
          </span>
        </>
      );
    default:
      throw new Error(item satisfies never);
  }
}

/** The silhouette of one result row: its tile, then its two lines. */
function ItemSkeleton({
  shape,
}: {
  shape: "article" | "entry";
}): React.JSX.Element {
  const title = <Skeleton className="rounded" width="70%" height={16} />;
  const caption = <Skeleton className="rounded" width="35%" height={12} />;
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Skeleton className="shrink-0 rounded-lg" width={32} height={32} />
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        {shape === "article" ? (
          <>
            {caption}
            {title}
          </>
        ) : (
          <>
            {title}
            {caption}
          </>
        )}
      </div>
    </div>
  );
}

// ── Palette ─────────────────────────────────────────────────────────────

/**
 * Escape closes the palette from anywhere while it is up, not only from its
 * field: a click inside the dialog can leave the focus elsewhere.
 *
 * The keypress stops here. Other window listeners (the article figure
 * panel's Escape) would otherwise receive the same key after the palette has
 * already closed, and close what sits behind it too.
 */
function useCloseOnEscape(onClose: () => void): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    }
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  });
}

/**
 * The desktop search palette (Figma "Search palette · 640"): a field over
 * grouped results, driven from the keyboard like a combobox. ↑↓ move the
 * active item, ↵ opens it, Esc closes; the pointer does the same.
 *
 * It only draws. What the groups hold and what opening an item does belong to
 * the caller.
 */
function DesktopSearchPalette({
  query,
  onQueryChange,
  groups,
  isLoading,
  onSelect,
  onClose,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  groups: readonly DesktopSearchPaletteGroup[];
  /** A search is in flight: the results under it are the previous ones. */
  isLoading: boolean;
  onSelect: (item: DesktopSearchPaletteItem) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const listboxId = useId();
  // The active item, for the query it was chosen under. A new query (typed, or
  // set by picking a topic) or a key no longer in the list falls back to the
  // first item, which is how a new search re-selects "Ask".
  const [activeChoice, setActive] = useState<{
    key: string;
    query: string;
  } | null>(null);
  const activeKey =
    activeChoice !== null && activeChoice.query === query
      ? activeChoice.key
      : null;
  function setActiveKey(key: string) {
    setActive({ key, query });
  }

  useCloseOnEscape(onClose);

  const items = groups.flatMap((group) =>
    group.status === "ready" ? group.items : []
  );
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.key === activeKey)
  );
  const active = items.at(activeIndex);
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  function moveActive(step: -1 | 1) {
    if (items.length === 0) return;
    const next = (activeIndex + step + items.length) % items.length;
    const item = items[next];
    if (item === undefined)
      throw new Error("should not happen: a wrapped index is out of range");
    setActiveKey(item.key);
    const option = document.getElementById(optionId(next));
    if (option === null)
      throw new Error("should not happen: the active option is not rendered");
    option.scrollIntoView({ block: "nearest" });
  }

  function optionProps(item: DesktopSearchPaletteItem, index: number) {
    return {
      id: optionId(index),
      role: "option",
      "aria-selected": index === activeIndex,
      onMouseMove: () => {
        if (item.key !== activeKey) setActiveKey(item.key);
      },
      // The field keeps the focus, so the keyboard still drives the palette
      // after a click that leaves it open (a topic).
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault();
      },
      onClick: () => {
        onSelect(item);
      },
    } as const;
  }

  let index = -1;

  return createPortal(
    <RemoveScroll enabled>
      <div
        className="fixed inset-0 z-1000 flex items-start justify-center bg-ds-black/25 px-6 pt-24"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("Search")}
          className="flex max-h-[calc(100dvh-8rem)] w-full max-w-160 flex-col overflow-hidden rounded-2xl border border-ds-slate-100 bg-ds-white shadow-search-palette scrollbar-subtle"
        >
          <div className="flex shrink-0 items-center gap-3 border-b border-ds-slate-100 py-4 pr-4 pl-5">
            <IconSearch
              size={20}
              stroke={1.5}
              className="shrink-0 text-ds-slate-600"
            />
            <input
              autoFocus
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={
                active === undefined ? undefined : optionId(activeIndex)
              }
              value={query}
              placeholder={t(
                "Search articles, Rooms, topics, or ask JuisciGPT"
              )}
              onChange={(e) => {
                onQueryChange(e.target.value);
              }}
              onKeyDown={(e) => {
                switch (e.key) {
                  case "ArrowDown":
                    e.preventDefault();
                    moveActive(1);
                    return;
                  case "ArrowUp":
                    e.preventDefault();
                    moveActive(-1);
                    return;
                  case "Enter":
                    e.preventDefault();
                    if (active !== undefined) onSelect(active);
                    return;
                  // The field is the dialog's only focusable control: Tab
                  // stays in it rather than reaching the page behind.
                  case "Tab":
                    e.preventDefault();
                    return;
                  default:
                    return;
                }
              }}
              className="min-w-0 flex-1 bg-transparent font-dm-sans text-heading-xs leading-6 font-normal text-ds-black outline-none placeholder:text-ds-slate-600"
            />
            <Kbd>Esc</Kbd>
          </div>

          <div
            id={listboxId}
            role="listbox"
            aria-busy={isLoading}
            className="flex min-h-0 flex-col gap-0.5 overflow-y-auto p-2"
          >
            {groups.map((group) => {
              if (group.status === "loading")
                return (
                  <div
                    key={group.key}
                    aria-hidden="true"
                    className="flex flex-col gap-0.5"
                  >
                    <div className="px-3 pt-2.5 pb-1 text-caption font-medium tracking-wider text-ds-slate-600 uppercase">
                      {group.title}
                    </div>
                    {Array.from({ length: group.rows }).map((_, row) => (
                      <ItemSkeleton key={row} shape={group.shape} />
                    ))}
                  </div>
                );
              const chips = group.items.every(
                (item) => item.kind === "topic-chip"
              );
              return (
                <div
                  key={group.key}
                  role="group"
                  aria-label={group.title === null ? undefined : group.title}
                  className="flex flex-col gap-0.5"
                >
                  {group.title === null ? null : (
                    <div className="px-3 pt-2.5 pb-1 text-caption font-medium tracking-wider text-ds-slate-600 uppercase">
                      {group.title}
                    </div>
                  )}
                  <div
                    className={
                      chips
                        ? "flex flex-wrap gap-1.5 px-3 pt-1 pb-2"
                        : "flex flex-col gap-0.5"
                    }
                  >
                    {group.items.map((item) => {
                      index += 1;
                      const isActive = index === activeIndex;
                      switch (item.kind) {
                        case "topic-chip":
                          return (
                            <div
                              key={item.key}
                              {...optionProps(item, index)}
                              className={cn(
                                "cursor-pointer rounded px-2 py-1 text-caption font-medium",
                                isActive
                                  ? "bg-ds-mint-50 text-ds-primary"
                                  : "bg-ds-black/4 text-ds-black/46"
                              )}
                            >
                              # {item.name}
                            </div>
                          );
                        case "see-all":
                          return (
                            <div
                              key={item.key}
                              {...optionProps(item, index)}
                              className={cn(
                                "flex cursor-pointer items-center gap-1 rounded-xl py-1.5 pr-3 pl-14 text-footnote font-semibold text-ds-primary",
                                isActive ? "bg-ds-mint-50" : null
                              )}
                            >
                              {item.label}
                              <IconChevronRight size={16} stroke={1.25} />
                            </div>
                          );
                        default:
                          return (
                            <div
                              key={item.key}
                              {...optionProps(item, index)}
                              className={cn(
                                "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2",
                                isActive ? "bg-ds-mint-50" : null
                              )}
                            >
                              <ItemBody item={item} query={query} />
                              {isActive ? <Kbd>↵</Kbd> : null}
                            </div>
                          );
                      }
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex shrink-0 items-center gap-4 border-t border-ds-slate-100 px-4 py-2.5 text-caption text-ds-slate-600">
            <span className="flex items-center gap-1.5">
              <Kbd>↑↓</Kbd>
              {t("Navigate")}
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>↵</Kbd>
              {t("Open")}
            </span>
            <span className="flex items-center gap-1.5">
              <Kbd>Esc</Kbd>
              {t("Close")}
            </span>
          </div>
        </div>
      </div>
    </RemoveScroll>,
    document.body
  );
}

export {
  DesktopSearchPalette,
  type DesktopSearchPaletteGroup,
  type DesktopSearchPaletteItem,
};
